import React, { useCallback, useEffect, useState } from "react";
import { ErrorBox } from "./common";
import MediaGallery from "./MediaGallery";
import MediaUploader from "./MediaUploader";
import { canDelete, errorMessage } from "../dmApi";

const KINDS = [
  { key: "", label: "All" },
  { key: "image", label: "Images" },
  { key: "video", label: "Videos" },
  { key: "pdf", label: "PDFs" },
];

/** Project media library (Media Library tab): upload, browse, link to content, delete. */
function MediaLibrary({ api, meta, projectId, content, onChange }) {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setItems((await api.listMedia(projectId, kind ? { kind } : undefined)) || []);
    } catch (err) {
      setError(errorMessage(err, "Could not load media"));
      setItems([]);
    }
  }, [api, projectId, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      await load();
      onChange?.();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const remove = (media) => {
    if (window.confirm(`Delete "${media.Original_Name || "this file"}" from storage?`)) {
      run(() => api.deleteMedia(media.Media_Id));
    }
  };

  const link = (media, contentId) => run(() => api.updateMedia(media.Media_Id, { Content_Id: contentId || null }));

  const mediaContent = (content || []).filter((c) => c.Content_Type !== "Keywords" && c.Content_Type !== "Text");

  return (
    <div className="dmp-card">
      <div className="dmp-header dmp-header--section" style={{ marginBottom: 12 }}>
        <div className="dmp-pill-toggles">
          {KINDS.map((k) => (
            <button
              key={k.key || "all"}
              type="button"
              className={`dmp-pill-toggle${kind === k.key ? " is-on" : ""}`}
              onClick={() => setKind(k.key)}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>
      <MediaUploader
        api={api}
        projectId={projectId}
        meta={meta}
        onUploaded={(media) => {
          setItems((list) => (kind && media.Media_Kind !== kind ? list : [media, ...(list || [])]));
          onChange?.();
        }}
      />
      <ErrorBox message={error} />
      <div style={{ marginTop: 14 }}>
        {items == null ? (
          <div className="dmp-empty">Loading…</div>
        ) : (
          <MediaGallery
            items={items}
            onDelete={remove}
            canDelete={(media) => canDelete(api, media)}
            emptyText="No posters, images or videos uploaded for this project yet."
            renderExtra={(media) => (
              <select
                className="dmp-media-tile__link"
                value={media.Content_Id || ""}
                onChange={(e) => link(media, e.target.value ? Number(e.target.value) : null)}
                title="Linked content"
              >
                <option value="">Not linked to content</option>
                {mediaContent.map((c) => (
                  <option key={c.Content_Id} value={c.Content_Id}>
                    {c.Content_Type}: {c.Title}
                  </option>
                ))}
              </select>
            )}
          />
        )}
      </div>
    </div>
  );
}

export default MediaLibrary;
